import { useState } from "react";
import { useData } from "../../context/DataContext";
import { Card, CardHead, Btn, Label, TextInput, NumberInput, DateInput, NativeSelect, TableWrap, Th, Td, Pill, EmptyState, IconBtn } from "../kit";
import { fmtINR, monthKey, monthLabel, EXPENSE_CATEGORIES, createSpreadExpenseEntries } from "../../lib/finance";
import { toast } from "sonner";
import { Trash2, Repeat } from "lucide-react";

export default function Expenses() {
  const { entries, saveEntries } = useData();
  const [cat, setCat] = useState("all");
  const [month, setMonth] = useState("all");
  const [editId, setEditId] = useState(null);
  const [ev, setEv] = useState({});
  const [spread, setSpread] = useState(null); // {groupId, category, desc, total, months, start}

  const months = Array.from(new Set(entries.filter((e) => e.type === "expense").map((e) => monthKey(e.date)))).sort().reverse();

  let rows = entries.filter((e) => e.type === "expense");
  if (cat !== "all") rows = rows.filter((e) => e.category === cat);
  if (month !== "all") rows = rows.filter((e) => monthKey(e.date) === month);
  rows = rows.slice().sort((a, b) => b.date.localeCompare(a.date));

  const startEdit = (e) => { setEditId(e.id); setEv({ date: e.date, category: e.category, desc: e.baseDescription || e.description, amount: e.amount }); };
  const saveEdit = async () => {
    await saveEntries(entries.map((e) => {
      if (e.id !== editId) return e;
      const base = { ...e, date: ev.date, category: ev.category, amount: parseFloat(ev.amount) || 0 };
      if (e.groupId) { base.baseDescription = ev.desc; base.description = `${ev.desc} (${e.groupIndex}/${e.groupMonths} of ₹${Math.round(e.groupTotal).toLocaleString("en-IN")})`; }
      else base.description = ev.desc;
      return base;
    }));
    setEditId(null); toast.success("Expense updated");
  };
  const del = async (e) => {
    if (e.groupId) {
      const all = window.confirm(`Part of a ${e.groupMonths}-month spread. OK = delete the ENTIRE spread, Cancel = just this month.`);
      await saveEntries(entries.filter((x) => all ? x.groupId !== e.groupId : x.id !== e.id));
      toast.success(all ? "Entire spread deleted" : "Month deleted");
    } else {
      if (!window.confirm("Delete this expense?")) return;
      await saveEntries(entries.filter((x) => x.id !== e.id));
      toast.success("Expense deleted");
    }
  };
  const openSpread = (groupId) => {
    const g = entries.filter((e) => e.groupId === groupId).sort((a, b) => a.date.localeCompare(b.date));
    if (!g.length) return;
    const f = g[0];
    setSpread({ groupId, category: f.category, desc: f.baseDescription || f.description, total: f.groupTotal, months: f.groupMonths, start: f.date });
  };
  const saveSpread = async () => {
    const { groupId, category, desc, total, months: m, start } = spread;
    const t = parseFloat(total), mi = parseInt(m);
    if (!desc || !t || t <= 0 || !mi || mi < 1 || !start) return toast.error("Fill in all fields");
    const without = entries.filter((e) => e.groupId !== groupId);
    await saveEntries([...without, ...createSpreadExpenseEntries(category, desc, t, mi, start, groupId)]);
    setSpread(null); toast.success(`Spread updated — ₹${Math.round(t).toLocaleString("en-IN")} across ${mi} months`);
  };
  const delSpread = async () => {
    if (!window.confirm("Delete this entire spread?")) return;
    await saveEntries(entries.filter((e) => e.groupId !== spread.groupId));
    setSpread(null); toast.success("Spread deleted");
  };

  return (
    <Card>
      <CardHead title="Expenses" />
      <div className="flex flex-wrap gap-2 px-5 mt-4">
        <NativeSelect value={cat} onChange={(e) => setCat(e.target.value)} className="!w-auto" data-testid="expense-category-filter">
          <option value="all">All categories</option>
          {EXPENSE_CATEGORIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
        </NativeSelect>
        <NativeSelect value={month} onChange={(e) => setMonth(e.target.value)} className="!w-auto">
          <option value="all">All months</option>
          {months.map((k) => <option key={k} value={k}>{monthLabel(k)}</option>)}
        </NativeSelect>
      </div>

      {spread && (
        <div className="mx-5 mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4">
          <h3 className="font-semibold text-slate-800">Edit spread expense</h3>
          <Label>Category</Label>
          <NativeSelect value={spread.category} onChange={(e) => setSpread({ ...spread, category: e.target.value })}>
            {EXPENSE_CATEGORIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </NativeSelect>
          <Label>Description</Label><TextInput value={spread.desc} onChange={(e) => setSpread({ ...spread, desc: e.target.value })} />
          <Label>Total amount (₹)</Label><NumberInput value={spread.total} onChange={(e) => setSpread({ ...spread, total: e.target.value })} />
          <Label>Spread across months</Label><NumberInput value={spread.months} onChange={(e) => setSpread({ ...spread, months: e.target.value })} />
          <Label>Start date</Label><DateInput value={spread.start} onChange={(e) => setSpread({ ...spread, start: e.target.value })} />
          <div className="flex gap-2 mt-3"><Btn onClick={saveSpread}>Save spread</Btn><Btn variant="danger" onClick={delSpread}>Delete spread</Btn><Btn variant="ghost" onClick={() => setSpread(null)}>Cancel</Btn></div>
        </div>
      )}

      <div className="mt-4">
        {rows.length === 0 ? <EmptyState>No expenses yet. Add one from the "Add Entry" tab.</EmptyState> : (
          <TableWrap>
            <thead><tr><Th>Date</Th><Th>Category</Th><Th>Description</Th><Th className="text-right">Amount</Th><Th></Th></tr></thead>
            <tbody>
              {rows.map((e) => editId === e.id ? (
                <tr key={e.id} className="bg-slate-50">
                  <Td><input type="date" className="rounded-lg border border-slate-200 px-2 py-1 text-sm" value={ev.date} onChange={(x) => setEv({ ...ev, date: x.target.value })} /></Td>
                  <Td>
                    <select className="rounded-lg border border-slate-200 px-2 py-1 text-sm" value={ev.category} onChange={(x) => setEv({ ...ev, category: x.target.value })}>
                      {EXPENSE_CATEGORIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                    </select>
                  </Td>
                  <Td><input className="w-full rounded-lg border border-slate-200 px-2 py-1 text-sm" value={ev.desc} onChange={(x) => setEv({ ...ev, desc: x.target.value })} /></Td>
                  <Td><input type="number" className="w-24 rounded-lg border border-slate-200 px-2 py-1 text-sm text-right" value={ev.amount} onChange={(x) => setEv({ ...ev, amount: x.target.value })} /></Td>
                  <Td><div className="flex gap-1"><IconBtn onClick={saveEdit} className="text-emerald-600">✓</IconBtn><IconBtn onClick={() => setEditId(null)}>✕</IconBtn></div></Td>
                </tr>
              ) : (
                <tr key={e.id} className="hover:bg-slate-50">
                  <Td className="text-slate-500">{e.date}</Td>
                  <Td><Pill tone="expense">{e.category}</Pill></Td>
                  <Td>{e.baseDescription || e.description}{e.groupId && <span className="ml-2 text-xs text-slate-400">{e.groupIndex}/{e.groupMonths}</span>}</Td>
                  <Td className="text-right tnum text-rose-600 font-semibold">{fmtINR(e.amount)}</Td>
                  <Td>
                    <div className="flex gap-1">
                      {e.groupId && <IconBtn title="Edit whole spread" onClick={() => openSpread(e.groupId)}><Repeat className="h-4 w-4" /></IconBtn>}
                      <IconBtn title="Edit" onClick={() => startEdit(e)}>✎</IconBtn>
                      <IconBtn title="Delete" onClick={() => del(e)} className="hover:text-rose-600"><Trash2 className="h-4 w-4" /></IconBtn>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </div>
      <div className="h-4" />
    </Card>
  );
}
